/**
 * File validation utilities for task creation form.
 * Mirrors backend validation in fastapi/app/services/execution/file_validation.py
 */

export const ALLOWED_TYPES = [
	// Images (Claude can see via Read tool)
	"image/jpeg",
	"image/png",
	"image/gif",
	"image/webp",
	// Documents
	"application/pdf",
	"text/plain",
	"text/markdown",
	// Code (text-based)
	"text/x-python",
	"application/javascript",
	"application/json",
	"text/csv",
	// Spreadsheets
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB per file
export const MAX_TOTAL_SIZE = 50 * 1024 * 1024; // 50MB total
export const MAX_FILE_COUNT = 10;

export interface FileValidationResult {
	valid: boolean;
	error?: string;
}

export function validateFiles(files: File[]): FileValidationResult {
	if (files.length > MAX_FILE_COUNT) {
		return { valid: false, error: `Maximum ${MAX_FILE_COUNT} files allowed` };
	}

	let totalSize = 0;
	for (const file of files) {
		if (!ALLOWED_TYPES.includes(file.type)) {
			return { valid: false, error: `${file.name}: Unsupported file type` };
		}
		if (file.size > MAX_FILE_SIZE) {
			return { valid: false, error: `${file.name}: File too large (max 20MB)` };
		}
		totalSize += file.size;
	}

	if (totalSize > MAX_TOTAL_SIZE) {
		return { valid: false, error: "Total file size exceeds 50MB" };
	}

	return { valid: true };
}

export function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
